import axios from "axios";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import Loader from "../../../components/Loader";
import Modal from "../../../components/Modal/Modal";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";
import AddExecutedData from "./AddExecutedData";

const initialData = {
  offer_id: "",
  property: { id: "", name: "" },
  company_gcl: "",
  contract_price: "",
  contract_cap_rate: "",
  gcl_broker_representing_seller: "",
  gcl_broker_representing_buyer: "",
  gcl_bds_seller: "",
  gcl_bds_buyer: "",
  created_at: "",
  gcl_broker_seller_share: [],
  gcl_broker_buyer_share: [],
};

const EditExecutedModal = ({ showModal, onClose, onCallApi, id }) => {
  const [config] = useAuth();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  const [disable, setDisable] = useState(false);
  const [addexecutedData, setExecutedData] = useState(initialData);

  const [isDollar, setIsDollar] = useState(false);
  const [isContractCapRateDollar, setIsContractCapRateDollar] = useState(false);
  const [isSellerBDSDollar, setIsSellerBDSDollar] = useState(false);
  const [isBuyerBDSDollar, setIsBuyerBDSDollar] = useState(false);
  const [isSellerBrokerDollar, setIsSellerBrokerDollar] = useState(false);
  const [isBuyerBrokerDollar, setIsBuyerBrokerDollar] = useState(false);

  useEffect(() => {
    if (id && showModal) {
      setLoading(true);
      axios
        .get(`${BASE_URL}/getby-id-executed/${id}`, config)
        .then((res) => {
          const data = res?.data?.executed;

          // Map broker shares to include an `isDollar` boolean for easier UI handling
          const sellerShares = (data?.gcl_broker_seller_share || []).map((item) => ({
            ...item,
            isDollar: item.type === "dollar",
          }));

          const buyerShares = (data?.gcl_broker_buyer_share || []).map((item) => ({
            ...item,
            isDollar: item.type === "dollar",
          }));

          setExecutedData({
            offer_id: data?.offer_id || "",
            property: {
              id: data?.link?.id || "",
              name: data?.link?.property_name || "",
            },
            portfolio: data?.portfolio === 1 ? 1 : 0,
            company_gcl: data?.company_gcl || "",
            contract_price: data?.contract_price || "",
            contract_cap_rate: data?.contract_cap_rate || "",
            gcl_broker_representing_seller: data?.gcl_broker_representing_seller || "",
            gcl_broker_representing_buyer: data?.gcl_broker_representing_buyer || "",
            gcl_bds_seller: data?.gcl_bds_seller || "",
            gcl_bds_buyer: data?.gcl_bds_buyer || "",
            created_at: data?.created_at || "",
            gcl_broker_seller_share: sellerShares,
            gcl_broker_buyer_share: buyerShares,

            // Fetching all type fields from API
            company_gcl_type: data?.company_gcl_type || "percent",
            contract_cap_rate_type: data?.contract_cap_rate_type || "percent",
            seller_bds_type: data?.seller_bds_type || "percent",
            buyer_bds_type: data?.buyer_bds_type || "percent",
            seller_broker_type: data?.seller_broker_type || "percent",
            buyer_broker_type: data?.buyer_broker_type || "percent",
          });

          // Set toggle states (for dollar/percent switches)
          setIsDollar(data?.company_gcl_type === "dollar");
          setIsContractCapRateDollar(data?.contract_cap_rate_type === "dollar");
          setIsSellerBDSDollar(data?.seller_bds_type === "dollar");
          setIsBuyerBDSDollar(data?.buyer_bds_type === "dollar");
          setIsSellerBrokerDollar(data?.seller_broker_type === "dollar");
          setIsBuyerBrokerDollar(data?.buyer_broker_type === "dollar");
        })
        .catch((err) => {
          if (
            err.response?.data?.message &&
            err.response?.data?.message !== "Too Many Attempts."
          ) {
            NotificationManager.error(err.response?.data?.message);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [id, showModal]);


  const handleClose = () => {
    setExecutedData(initialData);
    onClose();
    setError();
    setDisable(false);
  };

  const handleChange = (value, name) => {
    setError({ ...error, [name]: "" });
    setExecutedData({ ...addexecutedData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setDisable(true);

    const dataToSend = {
      offer_id: addexecutedData.offer_id,
      property: addexecutedData.property,
      company_gcl: addexecutedData.company_gcl,
      contract_price: addexecutedData.contract_price,
      contract_cap_rate: addexecutedData.contract_cap_rate,
      gcl_broker_representing_seller: addexecutedData.gcl_broker_representing_seller,
      gcl_broker_representing_buyer: addexecutedData.gcl_broker_representing_buyer,
      gcl_bds_seller: addexecutedData.gcl_bds_seller,
      gcl_bds_buyer: addexecutedData.gcl_bds_buyer,
      created_at: addexecutedData.created_at,
      portfolio: addexecutedData?.portfolio === 1 ? 1 : 0,

      company_gcl_type: isDollar ? "dollar" : "percent",
      contract_cap_rate_type: isContractCapRateDollar ? "dollar" : "percent",
      seller_bds_type: isSellerBDSDollar ? "dollar" : "percent",
      buyer_bds_type: isBuyerBDSDollar ? "dollar" : "percent",
      seller_broker_type: isSellerBrokerDollar ? "dollar" : "percent",
      buyer_broker_type: isBuyerBrokerDollar ? "dollar" : "percent",

      gcl_broker_seller_share: addexecutedData.gcl_broker_seller_share?.map(item => ({
        id: item.id,
        name: item.name,
        percent: item.percent,
        share: item.share,
        type: item.isDollar ? "dollar" : "percent",
      })),

      gcl_broker_buyer_share: addexecutedData.gcl_broker_buyer_share?.map(item => ({
        id: item.id,
        name: item.name,
        percent: item.percent,
        share: item.share,
        type: item.isDollar ? "dollar" : "percent",
      })),

    };

    console.log("Submitting Data:", dataToSend);

    axios
      .post(`${BASE_URL}/update-executed/${id}`, dataToSend, config)
      .then((res) => {
        handleClose();
        onCallApi();
      })
      .catch((err) => {
        setError(err.response?.data?.errors);
        if (
          err.response?.data?.message &&
          err.response?.data?.message !== "Too Many Attempts."
        ) {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setDisable(false));
  };


  return (
    <Modal
      title={`Edit Executed LOI`}
      desc={`Edit the Executed LOI information.`}
      show={showModal}
      onClose={onClose}
    >
      {loading ? (
        <Loader />
      ) : (
        <AddExecutedData
          addexecutedData={addexecutedData}
          setExecutedData={setExecutedData}
          handleChange={handleChange}
          error={error}
          onsetExecutedData={(value) => setExecutedData(value)}
          onSetError={setError}
          handleSubmit={handleSubmit}
          onClose={handleClose}
          showModal={showModal}
          disable={disable}
          isDollar={isDollar}
          setIsDollar={setIsDollar}
          isContractCapRateDollar={isContractCapRateDollar}
          setIsContractCapRateDollar={setIsContractCapRateDollar}
          isSellerBDSDollar={isSellerBDSDollar}
          setIsSellerBDSDollar={setIsSellerBDSDollar}
          isBuyerBDSDollar={isBuyerBDSDollar}
          setIsBuyerBDSDollar={setIsBuyerBDSDollar}
          isSellerBrokerDollar={isSellerBrokerDollar}
          setIsSellerBrokerDollar={setIsSellerBrokerDollar}
          isBuyerBrokerDollar={isBuyerBrokerDollar}
          setIsBuyerBrokerDollar={setIsBuyerBrokerDollar}
          isEditMode={true}
        />
      )}
    </Modal>
  );
};

export default EditExecutedModal;
